// EXPERIMENT: sign_form_signer_carousel_colors_experiment — remove with experiment cleanup
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

import { useSignerCarouselColorsExperiment } from '../useSignerCarouselColorsExperiment';

type SignerCarouselColorsExperiment =
	PetitionDetailsPageUserData['experiments']['signFormSignerCarouselColorsExperiment'];

/** The assignment when the carousel was first seen for a petition. */
type AssignmentSnapshot = Readonly<{
	experiment: SignerCarouselColorsExperiment;
	isRealAssignment: boolean;
}>;

type ExposableAssignment = Readonly<{
	petitionId: string;
	experiment: SignerCarouselColorsExperiment;
}>;

/**
 * Keeps the unconditional `useTrackExperimentExposure` call a no-op until the current petition is treated,
 * without relying on the normalizer's forced `treated: false`.
 */
const INERT_EXPERIMENT = {
	type: 'experiment-service',
	experimentName: 'sign_form_signer_carousel_colors_experiment',
	variation: 'control',
	config: {},
	treated: false,
} as SignerCarouselColorsExperiment;

/**
 * Returns the callback the carousel calls on every view. Per petition, the first view fires
 * `sign_form_recent_signers_carousel_impression` (every arm, fallback and override included), and a real
 * assignment is treated and exposed once.
 *
 * The assignment is snapshotted at the first view: signing while the treat call is pending refreshes page
 * user data into a forced-control fallback, which would otherwise drop the exposure.
 *
 * A treat call that resolves `false` (bot, not yet treatable, network error) is not exposed and is retried on
 * the next view, with at most one call in flight per petition.
 */
export function useTreatSignerCarouselColorsExperiment(): (seenPetitionId: string, signerCount: number) => void {
	const utilityContext = useUtilityContext();
	const track = useTracking();
	const {
		data: {
			petition: { id },
		},
	} = usePetitionDetailsPageData();
	const { experiment, isRealAssignment } = useSignerCarouselColorsExperiment();
	const petitionId = `petition_${id}`;

	// Keyed per petition so an A → B → A journey does not report A twice; also the once-only impression guard.
	const snapshotsRef = useRef(new Map<string, AssignmentSnapshot>());
	const treatedPetitionIdsRef = useRef(new Set<string>());
	const inFlightPetitionIdsRef = useRef(new Set<string>());
	// A petition id, not a boolean, so an SPA navigation cannot expose off the previous petition's treatment.
	const [exposable, setExposable] = useState<ExposableAssignment | null>(null);

	const treat = useCallback(
		async (carouselPetitionId: string, snapshot: AssignmentSnapshot) => {
			inFlightPetitionIdsRef.current.add(carouselPetitionId);
			try {
				if (await treatExperiment(utilityContext, snapshot.experiment.experimentName)) {
					treatedPetitionIdsRef.current.add(carouselPetitionId);
					setExposable({ petitionId: carouselPetitionId, experiment: snapshot.experiment });
				}
			} catch (e: unknown) {
				// treatExperiment should never reject; if it does, $exposure drops for everyone, so report it.
				void utilityContext.errorReporter.report({
					error:
						e instanceof Error
							? e
							: new Error('useTreatSignerCarouselColorsExperiment: unexpected rejection from treatExperiment'),
					context: { feature: 'sign_form' },
					params: { experimentName: snapshot.experiment.experimentName },
				});
			} finally {
				inFlightPetitionIdsRef.current.delete(carouselPetitionId);
			}
		},
		[utilityContext],
	);

	const reportCarouselSeen = useCallback(
		(carouselPetitionId: string, signerCount: number) => {
			let snapshot = snapshotsRef.current.get(carouselPetitionId);
			if (!snapshot) {
				snapshot = { experiment, isRealAssignment };
				snapshotsRef.current.set(carouselPetitionId, snapshot);

				void track('sign_form_recent_signers_carousel_impression', {
					petition_id: carouselPetitionId,
					recent_signers_count: signerCount,
				});
				// A QA override only reaches useTrackExperimentExposure's override branch, which logs and sends nothing.
				if (snapshot.experiment.override) {
					setExposable({ petitionId: carouselPetitionId, experiment: snapshot.experiment });
				}
			}

			if (!snapshot.isRealAssignment) return;
			if (treatedPetitionIdsRef.current.has(carouselPetitionId)) return;
			if (inFlightPetitionIdsRef.current.has(carouselPetitionId)) return;
			void treat(carouselPetitionId, snapshot);
		},
		[track, experiment, isRealAssignment, treat],
	);

	// The resolved experiment stays `treated: false` after treatExperiment, so it is flipped locally.
	const experimentForExposure = useMemo<SignerCarouselColorsExperiment>(
		() => (exposable?.petitionId === petitionId ? { ...exposable.experiment, treated: true } : INERT_EXPERIMENT),
		[exposable, petitionId],
	);
	// Without the petition id, the per-user dedup would swallow the next petition's exposure after an SPA navigation.
	const trackExperimentExposure = useTrackExperimentExposure(experimentForExposure, { resetKeys: [petitionId] });

	useEffect(() => {
		if (!experimentForExposure.treated) return;
		void trackExperimentExposure();
	}, [experimentForExposure.treated, trackExperimentExposure]);

	return reportCarouselSeen;
}
